import { useContext, useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Typography,
  Divider,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { UserContext } from "src/Utils/Context/UserContext";
import {
  useOrganizationRole,
  useAttachRolePermissions,
  useDetachRolePermissions,
} from "src/data/organization/queries/staffRole";
import { useOrganizationPermissions } from "src/data/admin/queries/organization";
import { InfoCard } from "src/design/InfoCard";
import { CustomButton } from "src/design/CustomButton";
import { CustomLoadingButton } from "src/design/CustomLoadingButton";
import { SnackBar } from "src/design/SnackBar";
import { isOrganizationManageEnabled } from "src/helpers/FeatureCapabilityConfig";
import {
  colorTextPrimary,
  colorTextMuted,
  colorTextSecondary,
  colorBorder,
  colorChipBackground,
} from "src/design/tokens";

const RoleView = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { roleId } = useParams<{ roleId: string }>();
  const { user } = useContext(UserContext);
  const organizationId = user?.organizationId ?? null;
  const canManage = isOrganizationManageEnabled(user);

  const { data: role, isLoading: roleLoading } = useOrganizationRole(roleId);
  const { data: availablePermissions, isLoading: permissionsLoading } =
    useOrganizationPermissions(organizationId);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [snack, setSnack] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (role) {
      setSelectedIds(new Set(role.permissions.map((p) => p.id)));
    }
  }, [role?.id]);

  const attachMutation = useAttachRolePermissions();
  const detachMutation = useDetachRolePermissions();

  const isSaving = attachMutation.isPending || detachMutation.isPending;

  const handleToggle = (permissionId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(permissionId)) {
        next.delete(permissionId);
      } else {
        next.add(permissionId);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!roleId || !role) return;

    const currentIds = new Set(role.permissions.map((p) => p.id));
    const toAdd = [...selectedIds].filter((id) => !currentIds.has(id));
    const toRemove = [...currentIds].filter((id) => !selectedIds.has(id));

    try {
      if (toAdd.length > 0) {
        await attachMutation.mutateAsync({ roleId, permissionIds: toAdd });
      }
      if (toRemove.length > 0) {
        await detachMutation.mutateAsync({ roleId, permissionIds: toRemove });
      }
      setSnack({
        open: true,
        message: t("myOrganization.roleView.updateSuccess"),
        severity: "success",
      });
    } catch (err) {
      setSnack({
        open: true,
        message:
          err instanceof Error
            ? err.message
            : t("myOrganization.roleView.updateError"),
        severity: "error",
      });
    }
  };

  const isLoading = roleLoading || permissionsLoading;

  const groupedPermissions = (availablePermissions ?? []).reduce<
    Record<string, typeof availablePermissions>
  >((acc, perm) => {
    const key = perm!.capabilityName ?? "__ungrouped";
    if (!acc[key]) acc[key] = [];
    acc[key]!.push(perm!);
    return acc;
  }, {});

  return (
    <>
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          {role && (
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: colorTextPrimary }}
            >
              {role.name}
            </Typography>
          )}
        </Box>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={32} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <InfoCard
                title={t("myOrganization.roleView.permissions")}
                icon={<LockOutlinedIcon fontSize="small" />}
                action={
                  canManage ? (
                    <CustomLoadingButton
                      loading={isSaving}
                      onSubmit={handleSave}
                      label={t("common.saveChanges")}
                      color="success"
                      fullwidth={false}
                    />
                  ) : undefined
                }
              >
                {(availablePermissions ?? []).length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body2" sx={{ color: colorTextMuted }}>
                      {t("myOrganization.roleView.noPermissions")}
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: colorTextMuted }}
                      >
                        {t("myOrganization.roleView.selectedCount", {
                          count: selectedIds.size,
                          total: availablePermissions?.length ?? 0,
                        })}
                      </Typography>
                    </Box>

                    {Object.entries(groupedPermissions).map(
                      ([capabilityId, perms]) => (
                        <Box key={capabilityId} sx={{ mb: 3 }}>
                          {capabilityId !== "__ungrouped" && (
                            <>
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  backgroundColor: colorChipBackground,
                                  mb: 1,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                    color: colorTextSecondary,
                                  }}
                                >
                                  {capabilityId}
                                </Typography>
                              </Box>
                              <Divider sx={{ mb: 1.5 }} />
                            </>
                          )}
                          <Grid container spacing={0}>
                            {(perms ?? []).map((perm) => (
                              <Grid item xs={12} sm={6} key={perm!.id}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={selectedIds.has(perm!.id)}
                                      onChange={() => handleToggle(perm!.id)}
                                      disabled={isSaving || !canManage}
                                    />
                                  }
                                  label={
                                    <Box>
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: 500,
                                          color: colorTextPrimary,
                                        }}
                                      >
                                        {perm!.description ?? perm!.name}
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{
                                    width: "100%",
                                    mr: 0,
                                    py: 0.5,
                                    px: 1,
                                    borderRadius: 1,
                                    "&:hover": {
                                      backgroundColor: `${colorBorder}50`,
                                    },
                                  }}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      ),
                    )}
                  </Box>
                )}
              </InfoCard>
            </Grid>
          </Grid>
        )}
      </Box>

      <SnackBar
        open={snack.open}
        message={snack.message}
        severity={snack.severity}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      />
    </>
  );
};

export default RoleView;
